import { getAIRoute, getMediaRoute, getMediaRouteV3 } from '../api/apiRoutes';
import {
  axiosDeleteRequest,
  axiosGetPaginatedRequest,
  axiosGetRequest,
  axiosMultipartPostRequest,
  axiosPatchRequest,
  axiosPostRequest,
} from '../api/axios';
import { addEnvQueryParam } from '../api/helpers';

const getUploadImagesRoute = () => `${getMediaRoute()}/media/upload`;
const getUploadFileRoute = () => `${getMediaRoute()}/file/upload/room`;
const getFetchFileRoute = () => `${getMediaRoute()}/files/room`;
const getDeleteFileRoute = () => `${getMediaRoute()}/file/room`;
const getDeleteFilesRoute = () => `${getMediaRoute()}/files/room`;

const getUploadVideosRoute = () => `${getMediaRouteV3()}/media`;
const getDeleteAssetRoute = () => `${getMediaRouteV3()}/asset`;

export const fullVideoUploadRoute = (roomId: string) =>
  addEnvQueryParam(
    `${getUploadVideosRoute()}?isprivate=false&targettype=featuredstory&roomid=${roomId}`
  );

export const uploadImageCall = (
  formData: unknown,
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
) => {
  return axiosMultipartPostRequest(getUploadImagesRoute(), formData, undefined, onUploadProgress).catch(
    (error) => {
      error.displayMessage = error.data.exception_message;
      throw error;
    }
  );
};

export const uploadVideosCall = (formData: unknown, roomId: string) => {
  return axiosMultipartPostRequest(
    `${getUploadVideosRoute()}?isprivate=false&targettype=featuredstory&roomid=${roomId}`,
    formData
  );
};

export const deleteVideoAssetCall = (assetName: string, roomId: string) => {
  return axiosPostRequest(`${getDeleteAssetRoute()}?roomid=${roomId}`, {
    assetName: assetName,
  });
};

export const uploadFileToFeatureContentCall = (
  files: FormData,
  roomId: string,
  storyId: string,
  translationLanguages?: string[],
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
) => {
  if (!roomId || !storyId) {
    throw new Error('RoomId and StoryId are required');
  }
  if (translationLanguages?.length) {
    files.append('languages', JSON.stringify(translationLanguages));
  }
  return axiosMultipartPostRequest(
    `${getUploadFileRoute()}/${roomId}/story/${storyId}`,
    files,
    undefined,
    onUploadProgress
  );
};

export const fetchFeaturedContentFilesCall = (
  roomId: string,
  storyId: string
) => {
  if (!roomId || !storyId) {
    throw new Error('RoomId and StoryId are required');
  }
  return axiosGetPaginatedRequest(
    `${getFetchFileRoute()}/${roomId}/story/${storyId}/-1`
  );
};

export const deleteFilesFromFeaturedContentCall = (
  roomId: string,
  storyId: string,
  fileIds: string[]
) => {
  if (!roomId || !fileIds || !storyId) {
    throw new Error('RoomId, StoryId and FileId are required');
  }
  return axiosDeleteRequest(
    `${getDeleteFilesRoute()}/${roomId}/story/${storyId}`,
    fileIds
  );
};

export const uploadFileToRoomCall = (
  files: FormData,
  roomId: string,
  contentType: string,
  translationLanguages?: string[],
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
) => {
  if (!roomId || !contentType) {
    throw new Error('RoomId and Content type are required');
  }
  if (translationLanguages?.length) {
    files.append('languages', JSON.stringify(translationLanguages));
  }
  return axiosMultipartPostRequest(
    `${getUploadFileRoute()}/${roomId}?featuredcontenttype=${contentType}`,
    files,
    undefined,
    onUploadProgress
  );
};

export const deleteFileFromRoomCall = (roomId: string, fileId: string) => {
  if (!roomId || !fileId) {
    throw new Error('RoomId and Content type are required');
  }
  return axiosDeleteRequest(`${getDeleteFileRoute()}/${roomId}/${fileId}`);
};

export const translateDocumentCall = (
  roomId: string,
  documentId: string,
  languages: string[]
) => {
  if (!roomId || !documentId || !languages?.length) {
    throw new Error('RoomId, DocumentId and languages are required');
  }
  return axiosPostRequest(`${getAIRoute()}/translation/room/${roomId}/document/${documentId}`, {
    languages,
  });
};

export const fetchRoomFilesCall = (roomId: string, numrecs = 4) => {
  if (!roomId) {
    throw new Error('RoomId is required');
  }
  return axiosGetPaginatedRequest(
    `${getDeleteFilesRoute()}/${roomId}/${numrecs}`
  );
};

export const deleteFilesFromRoomCall = (
  roomId: string,
  documentIds: string[]
) => {
  if (!roomId || !documentIds?.length) {
    throw new Error('RoomId and documentIds are required');
  }
  return axiosDeleteRequest(
    `${getDeleteFilesRoute()}/${roomId}`,
    documentIds
  );
};

export const fetchRoomFileCountCall = (roomId: string) => {
  if (!roomId) {
    throw new Error('RoomId is required');
  }
  return axiosGetRequest<{ count: number }>(
    `${getFetchFileRoute()}/${roomId}/count`
  );
};

export const updateRoomFileCall = (
  roomId: string,
  documentId: string,
  data: Record<string, unknown>
) => {
  if (!roomId || !documentId) {
    throw new Error('RoomId and DocumentId are required');
  }
  return axiosPatchRequest(
    `${getDeleteFileRoute()}/${roomId}/${documentId}`,
    data
  );
};
